<template>
    <div
        v-if="isInspectDrawer"
        class="checkpoint-inspection-right-pane checkpoint-inspection-right-pane--drawer"
        data-testid="checkpoint-inspection-drawer"
    >
        <AsEmployeeCompleteCheckpointRightPaneHeader
            class="checkpoint-inspection-right-pane__header"
            title="Inspect Checkpoint"
            close-position="left"
            :employee-name="employeeName"
            :first-name="firstName"
            :last-name="lastName"
            :job-title="jobTitle"
            :responsibility-name="responsibilityName"
            :avatar="avatar"
            :level="level"
            :criteria="criteria"
            @close="emit('close')"
        />

        <div
            v-if="perStepActive"
            class="checkpoint-inspection-right-pane__progress"
            data-testid="checkpoint-inspection-progress"
        >
            <InspectionProgress :states="stepStates" />
        </div>

        <div class="checkpoint-inspection-right-pane__body" data-testid="checkpoint-inspection-body">
            <div class="checkpoint-inspection-right-pane__alert">
                <div class="checkpoint-inspection-right-pane__alert-icon" aria-hidden="true">
                    <img
                        :src="require('@components/assets/info-circle.svg')"
                        alt=""
                        class="checkpoint-inspection-right-pane__alert-icon-img"
                    />
                </div>
                <div class="checkpoint-inspection-right-pane__alert-text">
                    <template v-if="isInspectorOnly">
                        <p class="checkpoint-inspection-right-pane__alert-title">
                            This is an inspector only checkpoint where you use the criteria only to inspect.
                        </p>
                        <p class="checkpoint-inspection-right-pane__alert-subtitle">
                            You can also guide yourself reading the employee prep notes, if available
                        </p>
                    </template>
                    <template v-else>
                        <p class="checkpoint-inspection-right-pane__alert-title">
                            Follow the inspection protocol and pass, fail or mark as not applicable
                        </p>
                        <p class="checkpoint-inspection-right-pane__alert-subtitle">
                            {{ learningTypeHint }}
                        </p>
                    </template>
                </div>
            </div>

            <div v-if="isInspectorOnly" class="checkpoint-inspection-right-pane__content">
                <CheckpointPrepNoteBlock :note="employeePrepNote" />
                <p v-if="!hasPrepNote" class="checkpoint-inspection-right-pane__empty">
                    This checkpoint has no steps. Mark it Pass, Fail or Not Applicable.
                </p>
            </div>

            <div v-else class="checkpoint-inspection-right-pane__steps">
                <VideoWithChaptersView
                    v-if="showVideoWithChaptersView"
                    class="checkpoint-inspection-right-pane__video-view"
                    :video-url="videoPlaybackUrl"
                    :duration-seconds="videoDurationSeconds"
                    :chapters="displayChapters"
                    :summary-lead="summaryLead"
                    :summary-bullets="summaryBullets"
                />
                <div v-if="!showVideoWithChaptersView || perStepActive" class="checkpoint-inspection-right-pane__steps-list">
                    <div
                        v-for="(step, index) in protocolSteps"
                        :key="stepKey(step)"
                        class="checkpoint-inspection-right-pane__step"
                    >
                        <InstructionStepCardV2
                            v-if="perStepActive"
                            :category="stepCategory(step)"
                            :inspector-action="step.inspector_action"
                            :look-for="step.look_for"
                            :pass-criteria="step.pass_criteria"
                            :fail-criteria="step.fail_criteria"
                            :timestamp-ref="stepTimestampRef(step)"
                            :video-jump-url="stepVideoJumpUrl(step)"
                            :step-index="index + 1"
                            :step-count="protocolSteps.length"
                            :show-play="!showVideoWithChaptersView"
                            :result="stepResult(step)"
                            :comment="stepRow(step)?.comment || ''"
                            :inspected-by-name="stepInspectorName(step)"
                            :inspected-at="stepRow(step)?.inspected_at || ''"
                            :read-only="!stepsEditable"
                            :saving="Boolean(savingStepSequence) || isEvaluating"
                            @verdict="recordStep(step, $event)"
                            @timestamp-click="emit('timestamp-click', $event)"
                        />
                        <InstructionStepCard
                            v-else
                            :competence_category="step.competence_category"
                            :inspector_action="step.inspector_action"
                            :look_for="step.look_for"
                            :pass_criteria="step.pass_criteria"
                            :fail_criteria="step.fail_criteria"
                            :timestamp_ref="stepTimestampRef(step)"
                            :video_jump_url="stepVideoJumpUrl(step)"
                            @timestamp-click="emit('timestamp-click', $event)"
                        />
                    </div>
                    <p v-if="!protocolSteps.length" class="checkpoint-inspection-right-pane__empty">
                        No inspection protocol steps are available.
                    </p>
                </div>
            </div>
        </div>

        <footer class="checkpoint-inspection-right-pane__footer" data-testid="checkpoint-inspection-footer">
            <p
                v-if="footerReason"
                class="checkpoint-inspection-right-pane__footer-reason"
                aria-live="polite"
                data-testid="checkpoint-inspection-pass-reason"
            >
                {{ footerReason }}
            </p>
            <InspectionAction
                :view="inspectionView"
                :is-button-type-large="true"
                :pass-disabled="passDisabled"
                :is-passed="isPassed"
                :is-failed="isFailed"
                :is-not-applicable="isNotApplicable"
                :avatar="displayInspectedByAvatar"
                :updated_on="displayInspectedOn"
                :failed_reason="displayFailedReason"
                :not_applicable_reason="notApplicableReason"
                :updated_by="inspectedByMeta"
                @onPass="handlePassClick"
                @onFail="openReasonModal"
                @onNotApplicable="handleNotApplicableClick"
                @onClose="handleInspectionClose"
                @openChatter="emit('openChatter')"
            />
        </footer>

        <ReasonModal
            :show-dialog="showReasonModal"
            :title="reasonModalConfig.title"
            :description="reasonModalConfig.description"
            :place-holder="reasonModalConfig.placeHolder"
            :btn-label="reasonModalConfig.btnLabel"
            :btn-danger="reasonModalConfig.btnDanger"
            :btn-color="reasonModalConfig.btnColor"
            :value="reasonText"
            @input="reasonText = $event"
            @onAction="confirmReason"
            @onClose="closeReasonModal"
        />
    </div>
    <div v-else class="checkpoint-inspection-right-pane">
        <div class="checkpoint-inspection-right-pane-navigator">
            <CheckpointInspectionNavigator
                @showChecklistInspectionTab="showChecklistInspectionTab"
                @next="$emit('next')"
                @previous="$emit('previous')"
                @onPass="$emit('onPass')"
                @onFail="(reason) => $emit('onFail', reason)"
                @onNotApplicable="(reason) => $emit('onNotApplicable', reason)"
                @onClose="$emit('onClose')"
                :checkpoints="checkpoints"
                :currentCheckpoint="currentCheckpoint"
                :totalCheckpoint="checkpoints.length"
                :evaluationItem="evaluationItem"
                :hasFilter="hasFilter"
            />
        </div>
        <div class="checkpoint-inspection-right-pane-criteria-banner">
            <CriteriaBanner :level="levelForBanner" :criteria="legacyCriteria" :criteriaPrefix="criteriaPrefix" />
        </div>
        <div class="checkpoint-inspection-right-pane-horizontal-menu">
            <HorizontalMenu :items="menuItems" />
        </div>
        <div class="checkpoint-inspection-right-pane-microlearning-view-edit">
            <div v-if="legacyVideoSource">
                <MicrolearningViewEdit :video="legacyVideoSource" :error="mlError" :summary="mlSummary" :steps="mlSteps" :editable="editable" />
            </div>
            <div v-else>
                <div class="checkpoint-inspection-right-pane-microlearning-view-edit-no-video">
                    <p>No Instructions found</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
import {
    passCheckPointEvaluation,
    failCheckPointEvaluation,
    notApplicableCheckPointEvaluation,
    getCheckpointInspectionsByVersion,
    recordCheckpointInspectionStep,
} from '@/api';
import { getCheckpointVideoSignedUrl } from '@/api/responsibilityAPI';
import { proficiencyCheckpointErrorMessage } from '@/utils/proficiencyBadgeHelpers';
import { inspectionStepErrorMessage } from '@/utils/inspectionStepErrorMessage';
import {
    isApiVideoPlaybackUrl,
    needsSignedVideoUrlResolution,
} from '@/utils/activeCheckpointMicrolearningValidity';
import { isCloudflareSignedUrl } from '@/utils/simulatedCloudflareStreamWebhook';
import AsEmployeeCompleteCheckpointRightPaneHeader from '@components/AsEmployeeCompleteCheckpointRightPaneHeader/AsEmployeeCompleteCheckpointRightPaneHeader.vue';
import CheckpointInspectionNavigator from '@components/CheckpointInspectionNavigator/CheckpointInspectionNavigator.vue';
import CheckpointPrepNoteBlock from '@components/CheckpointPrepNoteBlock/CheckpointPrepNoteBlock.vue';
import CriteriaBanner from '@components/CriteriaBanner/CriteriaBanner.vue';
import HorizontalMenu from '@components/HorizontalMenu/HorizontalMenu.vue';
import InspectionAction from '@components/InspectionAction/InspectionAction.vue';
import InspectionProgress from '@components/InspectionProgress/InspectionProgress.vue';
import InstructionStepCard from '@components/InstructionStepCard/InstructionStepCard.vue';
import InstructionStepCardV2 from '@components/InstructionStepCardV2/InstructionStepCardV2.vue';
import MicrolearningViewEdit from '@components/MicrolearningViewEdit/MicrolearningViewEdit.vue';
import ReasonModal from '@components/ReasonModal/ReasonModal.vue';
import VideoWithChaptersView from '@components/VideoWithChaptersView/VideoWithChaptersView.vue';
import {
    LD_FLAG_IDEA_MDP_648,
    LD_FLAG_VIDEO_WITH_CHAPTERS_VIEW,
} from '@components/constants/launchDarklyFlagKeys';
import { buildCheckpointTitle } from '@components/utils/buildChecklistInspectionLevels';
import {
    isInspectorOnlyCheckpoint,
    mapVideoChapterStepsForView,
    resolveInspectionSteps,
    resolveVideoWithChaptersViewProps,
} from '@components/utils/buildCheckpointDrawerSections';

const PROFICIENCY_LEVEL_MAP = {
    1: 'Apprentice',
    2: 'Professional',
    3: 'Coach',
    4: 'Master',
};

const REASON_MODAL_CONFIG = {
    fail: {
        title: 'Add Criteria Failed Reason',
        description: 'Please provide a reason why this criteria was not met.',
        placeHolder: 'Explain why the criteria was not fulfilled or provide specific feedback.',
        btnLabel: 'Fail',
        btnDanger: true,
        btnColor: null,
    },
    not_applicable: {
        title: 'Mark Criteria as Not Applicable',
        description: 'Provide a reason why this criteria is not applicable.',
        placeHolder: 'Explain why the criteria is not applicable.',
        btnLabel: 'Mark as Not Applicable',
        btnDanger: false,
        btnColor: '#666666',
    },
};

const STATUS_VIEW_MAP = {
    passed: 'passed-evaluation',
    failed: 'failed-evaluation',
    pending: 'pending-evaluation',
    not_applicable: 'notapplicable-evaluation',
    notapplicable: 'notapplicable-evaluation',
    'n/a': 'notapplicable-evaluation',
    reset: 'reset-pending-evaluation',
    obsolete: 'obsolete-manual-evaluation',
};

const props = defineProps({
    /** Legacy navigator layout: checkpoints to step through (Path to Readiness, Employee Badges). */
    checkpoints: {
        type: Array,
        default: () => [],
    },
    currentCheckpoint: {
        type: Number,
        default: 0,
    },
    evaluationItem: {
        type: Object,
        default: null,
    },
    editable: {
        type: Boolean,
        default: false,
    },
    hasFilter: {
        type: Boolean,
        default: false,
    },
    /** Inspect drawer: merged checklist row + getEmployeeCheckpointDetail payload. Selects the drawer layout. */
    checkpointItem: {
        type: Object,
        default: null,
    },
    employeeName: {
        type: String,
        default: '',
    },
    firstName: {
        type: String,
        default: '',
    },
    lastName: {
        type: String,
        default: '',
    },
    jobTitle: {
        type: String,
        default: '',
    },
    avatar: {
        type: [String, Object],
        default: null,
    },
    responsibilityName: {
        type: String,
        default: '',
    },
    level: {
        type: String,
        default: 'Apprentice',
        validator: (value) => ['Apprentice', 'Professional', 'Coach', 'Master'].includes(value),
    },
    responsibilityId: {
        type: [String, Number],
        default: null,
    },
    employeeId: {
        type: [String, Number],
        default: null,
    },
    /** When true (Re-inspect), show Pass / Fail / Not Applicable like a fresh inspection. */
    reinspectMode: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    'showChecklistInspectionTab',
    'next',
    'previous',
    'onPass',
    'onFail',
    'onNotApplicable',
    'onClose',
    'close',
    'evaluated',
    'openChatter',
    'timestamp-click',
]);

const { proxy } = getCurrentInstance();

const isInspectDrawer = computed(() => props.checkpointItem !== null);

// ---- legacy navigator layout ----
const currentCheckpointData = computed(() => props.checkpoints[props.currentCheckpoint] ?? null);

const criteriaPrefix = computed(() => currentCheckpointData.value?.prefix ?? '');
const legacyCriteria = computed(() => currentCheckpointData.value?.criteria  ?? '');

const levelForBanner = computed(() => {
    const level = currentCheckpointData.value?.proficiency_level;
    return PROFICIENCY_LEVEL_MAP[level] ?? 'Apprentice';
});

const legacyVideoSource = computed(() =>
    currentCheckpointData.value?.microlearning_cloudflare_video_id
    || currentCheckpointData.value?.microlearning
    || currentCheckpointData.value?.microlearning_video_id
    || null
);

const mlInstructions = computed(() => currentCheckpointData.value?.microlearning_instructions ?? {});
const mlError = computed(() => mlInstructions.value?.error ?? null);
const mlSummary = computed(() => mlInstructions.value?.summary ?? null);
const mlSteps = computed(() => mlInstructions.value?.steps ?? []);

const menuItems = [
    {
        title: 'Inspection Instructions',
        count: null
    },
    {
        title: 'Tutorial',
        count: null
    },
    {
        title: 'Quiz',
        count: null
    }
];

const showChecklistInspectionTab = () => {
    emit('showChecklistInspectionTab');
};

// ---- inspect drawer layout ----
const videoWithChaptersViewEnabled = ref(false);
const resolvedCheckpointVideoUrl = ref('');
const showReasonModal = ref(false);
const reasonModalMode = ref('fail');
const reasonText = ref('');
const localInspection = ref(null);
const isEvaluating = ref(false);
const perStepFlagEnabled = ref(false);
const loadedSteps = ref([]);
const savingStepSequence = ref(null);
let stepsLoadToken = 0;

onMounted(async () => {
    if (!isInspectDrawer.value) return;
    try {
        videoWithChaptersViewEnabled.value =
            (await proxy?.$getFlag?.(LD_FLAG_VIDEO_WITH_CHAPTERS_VIEW, true)) === true;
    } catch {
        videoWithChaptersViewEnabled.value = true;
    }
    try {
        perStepFlagEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_648, false)) === true;
    } catch {
        perStepFlagEnabled.value = false;
    }
});

const checkpointId = computed(() => props.checkpointItem?.id ?? props.checkpointItem?.checkpoint_id ?? null);
const criteria = computed(() => (props.checkpointItem ? buildCheckpointTitle(props.checkpointItem) : ''));
const isInspectorOnly = computed(() => isInspectorOnlyCheckpoint(props.checkpointItem ?? {}));
const employeePrepNote = computed(() => props.checkpointItem?.employee_prep_note ?? null);
const hasPrepNote = computed(() => Boolean(String(employeePrepNote.value ?? '').trim()));
const protocolSteps = computed(() => resolveInspectionSteps(props.checkpointItem ?? {}));
const inspectionStatus = computed(
    () => props.checkpointItem?.activity_statuses?.inspection || props.checkpointItem?.overall_status || 'pending'
);

const isVideoLearning = computed(() => {
    const item = props.checkpointItem;
    if (!item) return true;
    if (item.has_active_video) return true;
    const type = String(item.active_microlearning_type || item.proposed_microlearning_type || '').toLowerCase();
    return type === 'video';
});

const isStepByStepVideo = computed(() => {
    const type =
        props.checkpointItem?.active_microlearning_type ||
        props.checkpointItem?.proposed_microlearning_type;
    return String(type ?? '').trim().toLowerCase() === 'video_step_by_step_tutorial';
});

const checkpointChapterModel = computed(() =>
    props.checkpointItem ? resolveVideoWithChaptersViewProps(props.checkpointItem) : null
);

function mapProtocolStepsToChapterRows(steps = []) {
    if (!Array.isArray(steps) || !steps.length) return [];
    return mapVideoChapterStepsForView(
        steps.map((step) => ({
            chapter_title: step.inspector_action,
            chapter_description: step.look_for,
            chapter_summary: step.pass_criteria,
            start_time: step.timestamp_ref,
            timestamp_ref: step.timestamp_ref,
            video_jump_url: step.video_jump_url,
            thumbnail: step.thumbnail ?? step.thumbnail_url ?? step.thumbnailUrl ?? '',
        }))
    );
}

const displayChapters = computed(() => {
    const fromDetail = checkpointChapterModel.value?.chapters ?? [];
    return fromDetail.length ? fromDetail : mapProtocolStepsToChapterRows(protocolSteps.value);
});

const planVideoUrl = computed(() => {
    const item = props.checkpointItem;
    return String(item?.active_microlearning || item?.microlearning || '').trim();
});

const checkpointVideoUrl = computed(() => {
    const fromDetail = String(checkpointChapterModel.value?.videoUrl ?? '').trim();
    return fromDetail || planVideoUrl.value;
});

const showVideoWithChaptersView = computed(
    () =>
        videoWithChaptersViewEnabled.value &&
        isVideoLearning.value &&
        !isInspectorOnly.value &&
        !isStepByStepVideo.value &&
        Boolean(checkpointVideoUrl.value)
);

const videoPlaybackUrl = computed(() => resolvedCheckpointVideoUrl.value || checkpointVideoUrl.value);
const videoDurationSeconds = computed(() => checkpointChapterModel.value?.durationSeconds ?? 0);
const summaryLead = computed(() => checkpointChapterModel.value?.summaryLead ?? '');
const summaryBullets = computed(() => checkpointChapterModel.value?.summaryBullets ?? []);

const learningTypeHint = computed(() =>
    isVideoLearning.value
        ? 'Click on timeline to watch the learning video corresponding to the step'
        : 'The learning part of this checkpoint is an article'
);

function resolveAccountId() {
    return localStorage.getItem('currentAccountID');
}

async function refreshCheckpointVideoPlaybackUrl() {
    const fallback = checkpointVideoUrl.value;
    if (!fallback) {
        resolvedCheckpointVideoUrl.value = '';
        return;
    }

    const needsSigned =
        needsSignedVideoUrlResolution(fallback, isCloudflareSignedUrl) &&
        !isApiVideoPlaybackUrl(fallback);
    const accountId = resolveAccountId();

    if (!needsSigned || checkpointId.value == null || props.responsibilityId == null || !accountId) {
        resolvedCheckpointVideoUrl.value = fallback;
        return;
    }

    try {
        const { data } = await getCheckpointVideoSignedUrl(accountId, props.responsibilityId, checkpointId.value);
        resolvedCheckpointVideoUrl.value = data?.url || fallback;
    } catch (error) {
        console.error('Error fetching checkpoint video signed URL:', error);
        resolvedCheckpointVideoUrl.value = fallback;
    }
}

watch(
    () => [checkpointVideoUrl.value, checkpointId.value, props.responsibilityId],
    refreshCheckpointVideoPlaybackUrl,
    { immediate: true }
);

watch([checkpointId, inspectionStatus], () => {
    localInspection.value = null;
});

watch(
    () => props.reinspectMode,
    (isReinspect) => {
        if (isReinspect) localInspection.value = null;
    }
);

const displayInspectionStatus = computed(() => localInspection.value?.status ?? inspectionStatus.value);
const displayInspectedBy = computed(
    () => localInspection.value?.inspectedBy ?? props.checkpointItem?.inspected_by?.name ?? ''
);
const displayInspectedByAvatar = computed(
    () => localInspection.value?.inspectedByAvatar ?? props.checkpointItem?.inspected_by?.picture ?? ''
);
const displayInspectedOn = computed(
    () => localInspection.value?.inspectedOn ?? props.checkpointItem?.inspected_on ?? ''
);
const displayFailedReason = computed(
    () => localInspection.value?.failedReason ?? props.checkpointItem?.failed_reason ?? ''
);
const notApplicableReason = computed(() => props.checkpointItem?.not_applicable_reason ?? '');

const normalizedStatus = computed(() =>
    String(displayInspectionStatus.value ?? 'pending')
        .toLowerCase()
        .replace(/[\s_]+/g, '_')
);

const inspectionView = computed(() => {
    const resolved = STATUS_VIEW_MAP[normalizedStatus.value] ?? 'pending-evaluation';
    if (
        props.reinspectMode &&
        !localInspection.value &&
        ['failed-evaluation', 'passed-evaluation', 'notapplicable-evaluation'].includes(resolved)
    ) {
        return 'pending-evaluation';
    }
    return resolved;
});
const isPassed = computed(() => normalizedStatus.value === 'passed');
const isFailed = computed(() => normalizedStatus.value === 'failed');
const isNotApplicable = computed(() =>
    ['not_applicable', 'notapplicable', 'n/a'].includes(normalizedStatus.value)
);

const inspectedByMeta = computed(() => ({
    name: displayInspectedBy.value || null,
    picture: displayInspectedByAvatar.value || null,
}));

const reasonModalConfig = computed(
    () => REASON_MODAL_CONFIG[reasonModalMode.value] || REASON_MODAL_CONFIG.fail
);

// ---- per-step inspection (MDP-648, Rule 14) ----
const FINAL_INSPECTION_STATUSES = ['passed', 'failed', 'not_applicable'];
const STEP_CATEGORIES = ['knowledge', 'ability', 'behaviour'];

const perStepActive = computed(
    () =>
        isInspectDrawer.value &&
        perStepFlagEnabled.value &&
        !isInspectorOnly.value &&
        protocolSteps.value.length > 0 &&
        !isNotApplicable.value
);
const stepsEditable = computed(() =>
    ['pending-evaluation', 'reset-pending-evaluation'].includes(inspectionView.value)
);

function stepCategory(step) {
    const category = String(step.competence_category ?? '').toLowerCase();
    return STEP_CATEGORIES.includes(category) ? category : 'knowledge';
}

function stepRow(step) {
    return loadedSteps.value.find((row) => row.step_sequence === step.step_sequence) ?? null;
}

function stepResult(step) {
    const state = stepRow(step)?.state;
    return state === 'passed' || state === 'failed' ? state : 'not_yet_inspected';
}

function stepInspectorName(step) {
    const inspector = stepRow(step)?.inspected_by;
    return [inspector?.first_name, inspector?.last_name].filter(Boolean).join(' ');
}

const stepStates = computed(() => protocolSteps.value.map(stepResult));
const failedStepCount = computed(() => stepStates.value.filter((state) => state === 'failed').length);
const pendingStepCount = computed(
    () => stepStates.value.filter((state) => state === 'not_yet_inspected').length
);
const allStepsPassed = computed(
    () => stepStates.value.length > 0 && failedStepCount.value === 0 && pendingStepCount.value === 0
);
const passDisabled = computed(() => perStepActive.value && stepsEditable.value && !allStepsPassed.value);

function countLabel(count) {
    return `${count} ${count === 1 ? 'step' : 'steps'}`;
}

const footerReason = computed(() => {
    if (!perStepActive.value || !stepsEditable.value) return '';
    if (allStepsPassed.value) return 'Every step is Pass, so you can pass the employee.';
    const reasons = [];
    if (pendingStepCount.value > 0) {
        reasons.push(
            `${countLabel(pendingStepCount.value)} ${pendingStepCount.value === 1 ? 'is' : 'are'} not yet inspected`
        );
    }
    if (failedStepCount.value > 0) reasons.push(`${countLabel(failedStepCount.value)} failed`);
    return `Pass the employee only when every step is Pass. ${reasons.join(' and ')}, so only Fail is available.`;
});

function pickInspectionSteps(data, reinspect) {
    const rows = (data?.version_groups ?? [])
        .flatMap((group) => group.rows ?? [])
        .filter((row) => Array.isArray(row?.inspection_steps?.steps));
    const eligible = reinspect ? rows.filter((row) => !FINAL_INSPECTION_STATUSES.includes(row.status)) : rows;
    if (!eligible.length) return [];
    const latest = eligible.reduce((a, b) => (b.id > a.id ? b : a));
    return latest.inspection_steps.steps;
}

async function loadInspectionSteps() {
    const token = ++stepsLoadToken;
    loadedSteps.value = [];
    if (!perStepActive.value) return;
    const accountId = resolveAccountId();
    const { responsibilityId, employeeId } = props;
    const id = checkpointId.value;
    if (!accountId || responsibilityId == null || id == null || employeeId == null) return;
    try {
        const { data } = await getCheckpointInspectionsByVersion(accountId, responsibilityId, id, {
            employee: employeeId,
        });
        if (token === stepsLoadToken) loadedSteps.value = pickInspectionSteps(data, props.reinspectMode);
    } catch (error) {
        console.error('Error loading inspection step results:', error);
    }
}

watch(
    () => [perStepActive.value, checkpointId.value, props.employeeId, props.reinspectMode],
    loadInspectionSteps,
    { immediate: true }
);

async function recordStep(step, { result, comment }) {
    if (savingStepSequence.value != null || isEvaluating.value || !stepsEditable.value) return;
    const accountId = resolveAccountId();
    const { responsibilityId, employeeId } = props;
    const id = checkpointId.value;
    if (!accountId || responsibilityId == null || id == null || employeeId == null) return;

    try {
        savingStepSequence.value = step.step_sequence;
        const { data } = await recordCheckpointInspectionStep(accountId, responsibilityId, id, {
            employee: employeeId,
            step_sequence: step.step_sequence,
            result,
            comment,
        });
        if (Array.isArray(data?.steps)) loadedSteps.value = data.steps;
    } catch (error) {
        proxy.$notifications?.error(
            inspectionStepErrorMessage(error) ||
                proficiencyCheckpointErrorMessage(error) ||
                'Error saving the step result'
        );
        console.error('Error saving inspection step result:', error);
    } finally {
        savingStepSequence.value = null;
    }
}

function stepKey(step) {
    return step.id ?? `${step.competence_category}-${step.inspector_action}`;
}

function stepTimestampRef(step) {
    if (!isVideoLearning.value) return '';
    return step.timestamp_ref || '';
}

function stepVideoJumpUrl(step) {
    if (!isVideoLearning.value) return '';
    if (step.video_jump_url) return step.video_jump_url;
    return planVideoUrl.value;
}

function closeReasonModal() {
    showReasonModal.value = false;
    reasonModalMode.value = 'fail';
    reasonText.value = '';
}

function openReasonModal(mode = 'fail') {
    reasonModalMode.value = mode;
    reasonText.value = '';
    showReasonModal.value = true;
}

function handleNotApplicableClick() {
    openReasonModal('not_applicable');
}

function handleInspectionClose() {
    localInspection.value = null;
    emit('onClose');
}

const EVALUATION_REQUESTS = {
    passed: passCheckPointEvaluation,
    failed: failCheckPointEvaluation,
    not_applicable: notApplicableCheckPointEvaluation,
};

const SUCCESS_MESSAGES = {
    passed: 'Checkpoint marked as passed',
    failed: 'Checkpoint marked as failed',
    not_applicable: 'Checkpoint marked as not applicable',
};

const ERROR_MESSAGES = {
    passed: 'Error passing checkpoint',
    failed: 'Error failing checkpoint',
    not_applicable: 'Error marking checkpoint as not applicable',
};

async function submitEvaluation(status, reason) {
    if (isEvaluating.value) return;

    const accountId = resolveAccountId();
    const { responsibilityId, employeeId } = props;
    const id = checkpointId.value;
    if (!accountId || responsibilityId == null || id == null || employeeId == null) return;

    try {
        isEvaluating.value = true;
        proxy.$loader?.show();
        await EVALUATION_REQUESTS[status](accountId, responsibilityId, id, {
            employee: employeeId,
            checkpoint: id,
            reason,
            status,
        });
        closeReasonModal();
        const payload = {
            status,
            accountId,
            responsibilityId,
            checkpointId: id,
            employeeId,
            level: props.level,
        };
        if (status !== 'passed') payload.reason = reason;
        emit('evaluated', payload);
        emit('close');
        proxy.$notifications?.success(SUCCESS_MESSAGES[status]);
    } catch (error) {
        proxy.$notifications?.error(proficiencyCheckpointErrorMessage(error) || ERROR_MESSAGES[status]);
        console.error(`${ERROR_MESSAGES[status]}:`, error);
    } finally {
        isEvaluating.value = false;
        proxy.$loader?.hide();
    }
}

function handlePassClick() {
    if (passDisabled.value) return;
    return submitEvaluation('passed', 'passed');
}

function confirmReason() {
    const trimmedReason = reasonText.value?.trim();
    if (!trimmedReason) return;
    return submitEvaluation(reasonModalMode.value === 'not_applicable' ? 'not_applicable' : 'failed', trimmedReason);
}
</script>

<style scoped>
.checkpoint-inspection-right-pane-criteria-banner {
    padding: 10px 16px;
}
.checkpoint-inspection-right-pane-horizontal-menu {
    padding: 0px 16px;
}
.checkpoint-inspection-right-pane-microlearning-view-edit {
    padding: 0px 16px;
    margin-top: 15px;
}
:deep(.instruction-step-card) {
    border-radius: 4px;
    border: 0.5px solid #D0D5DD;
    padding: 10px;
}
.checkpoint-inspection-right-pane-microlearning-view-edit-no-video {
    padding: 0px 15px !important;
}

.checkpoint-inspection-right-pane--drawer {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    background: #ffffff;
    border-left: 1px solid #eaecf0;
    box-shadow:
        0 20px 24px -4px rgba(16, 24, 40, 0.08),
        0 8px 8px -4px rgba(16, 24, 40, 0.03);
    box-sizing: border-box;
    overflow: hidden;
}
.checkpoint-inspection-right-pane__header {
    flex-shrink: 0;
}
.checkpoint-inspection-right-pane__body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    gap: 24px;
    min-height: 0;
    padding: 24px 16px;
    overflow-y: auto;
    box-sizing: border-box;
}
.checkpoint-inspection-right-pane__progress {
    flex-shrink: 0;
    padding: 12px 16px;
    background: #ffffff;
    border-bottom: 1px solid #eaecf0;
    box-sizing: border-box;
}
.checkpoint-inspection-right-pane__footer-reason {
    align-self: stretch;
    margin: 0;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    text-align: right;
    color: #475467;
}
.checkpoint-inspection-right-pane__footer {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    flex-shrink: 0;
    padding: 16px;
    background: #ffffff;
    border-top: 1px solid #eaecf0;
    box-sizing: border-box;
}
.checkpoint-inspection-right-pane__alert {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 16px;
    width: 100%;
    padding: 16px;
    background: #ffffff;
    border: 1px solid #d0d5dd;
    border-radius: 12px;
    box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
    box-sizing: border-box;
}
.checkpoint-inspection-right-pane__alert-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    background: #ffffff;
    border: 1px solid #eaecf0;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}
.checkpoint-inspection-right-pane__alert-icon-img {
    width: 20px;
    height: 20px;
}
.checkpoint-inspection-right-pane__alert-text {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    gap: 4px;
    min-width: 0;
}
.checkpoint-inspection-right-pane__alert-title {
    margin: 0;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #344054;
}
.checkpoint-inspection-right-pane__alert-subtitle {
    margin: 0;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #475467;
}
.checkpoint-inspection-right-pane__footer :deep(.standard-button--terminated.standard-button--outline) {
    background-color: #000000;
    border-color: #000000;
    color: #ffffff;
}
.checkpoint-inspection-right-pane__content {
    flex: 1 1 auto;
}
.checkpoint-inspection-right-pane__steps-list {
    display: flex;
    flex-direction: column;
    gap: 22px;
}
.checkpoint-inspection-right-pane__empty {
    margin: 0;
    padding: 48px 16px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    color: #667085;
}
.checkpoint-inspection-right-pane__step {
    padding: 10px;
    box-sizing: border-box;
}
.checkpoint-inspection-right-pane__video-view {
    padding: 10px;
    box-sizing: border-box;
}
.checkpoint-inspection-right-pane__step :deep(.instruction-step-card) {
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    box-sizing: border-box;
}
.checkpoint-inspection-right-pane__step :deep(.instruction-step-card-timestamp-link) {
    padding-left: 5px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #2c0cfa;
}
.checkpoint-inspection-right-pane__step :deep(.instruction-step-card-content-wrapper) {
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    padding: 5px;
    box-sizing: border-box;
}
.checkpoint-inspection-right-pane__step :deep(.instruction-step-card-content-wrapper:hover) {
    background-color: transparent;
    cursor: default;
}
.checkpoint-inspection-right-pane__step :deep(.video-instruction-step-flex),
.checkpoint-inspection-right-pane__step :deep(.criteria-line) {
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #475467;
}
</style>